/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ApplicationsPackListPage Component
 * ------------------------
 * Displays all application packages.
 * Features:
 * 1. Fetches and lists application packages.
 * 2. Supports pagination, creation, and navigation to individual application package details.
 * 3. Provides error and loading states with user-friendly messages.
 *
 * Dependencies:
 * - APIHelper for backend API calls.
 * - ThemeContext for consistent UI theming.
 * - MessageBox, Pagination, and Button for UI elements.
 * - useAlertDialog for displaying confirmation/error dialogs.
 */

import React, { useEffect, useState } from 'react';
import ApplicationCard from '../components/ApplicationCard';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from "../config/envConfig";
import MessageBox from '../components/MessageBox';
import { useNavigate } from 'react-router-dom';
import { useAlertDialog } from '../components/common/AlertDialogContext';
import ProgressBarDialog from '../components/common/ProgressBar';
import Paginator from '../components/common/Paginator';
import { handleErrorResponse } from '../utils/utils';

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import ClientKeySecretDialog from '../components/common/ClientKeySecretDialog';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

const perPage = 18;

export default function MyPackagesListPage() {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [toggling, setToggling] = useState(false);

    const { showAlertDialog } = useAlertDialog();

    const [totalApplications, setTotalApplications] = useState(0);

    const packsQueryKey = ['myPackages', page, perPage];

    const packsQuery = useQuery({
        queryKey: packsQueryKey,
        queryFn: async ({ signal }) => {

            const url = `/arcee/application-pack/my-packages/?page=${page}&page_size=${perPage}`;
            const resp = await apiHelper.apiCall(url, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                signal,
            });

            if (!resp.ok) {
                await handleErrorResponse(resp);
                throw new Error('Failed to load application packages');
            }

            return resp.json();
        },
        placeholderData: keepPreviousData,
        staleTime: 30_000,
        retry: 1,
    });

    useEffect(() => {
        if (packsQuery.isLoading) {
            setLoading(true);
            setError(null);
            return;
        }

        if (packsQuery.isError) {
            setLoading(false);
            setApplications([]);
            setTotalApplications(0);
            setError(packsQuery.error?.message || 'Failed to fetch application packages');
            return;
        }

        if (packsQuery.data) {
            setApplications(packsQuery.data.results || []);
            setTotalApplications(packsQuery.data.count || 0);
            setLoading(false);
            setError(null);
        }
    }, [packsQuery.status, packsQuery.data, packsQuery.error]);

    const pageCount = Math.max(1, Math.ceil(totalApplications / perPage));
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pageCount) setPage(newPage);
    };
    const handlePrevious = () => setPage(p => Math.max(p - 1, 1));
    const handleNext = () => setPage(p => Math.min(p + 1, pageCount));

    const handleCopySeedCode = async (app) => {
        try {
            const resp = await apiHelper.apiCall(`/arcee/application-pack/${app.id}/seed-code/`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!resp.ok) {
                await handleErrorResponse(resp);
                throw new Error('Failed to generate seed code');
            }

            const data = await resp.json();
            await navigator.clipboard.writeText(data.seed_code || '');

            showAlertDialog({
                title: "Copied",
                message: `Seed code for "${app.name}" copied to clipboard. Paste it into seed_packs.py as the ${data.method_name}() method, then register it inside handle().`,
                type: "success",
                okLabel: "Close",
                showCancel: false,
            });
        } catch (err) {
            console.error('Copy seed code error:', err);
            showAlertDialog({
                title: "Copy Failed",
                message: err?.message || "Could not copy the seed code. Please try again.",
                type: "error",
                okLabel: "Close",
                showCancel: false,
            });
        }
    };

    const handleOpen = (app) => { navigate(`/dashboard/my-packages/${app.id}`); };
    const handleEdit = (app) => { alert('Edit: ' + app.name); };
    const handleDelete = (app) => { if (window.confirm('Delete ' + app.name + '?')) { /* API delete */ } };

    const handleToggleStatus = (app) => {
        const newStatus = app.status === 'draft' ? 'published' : 'draft';
        const actionText = newStatus === 'published' ? 'activate' : 'deactivate';

        showAlertDialog({
            title: `Confirm ${actionText.charAt(0).toUpperCase() + actionText.slice(1)}`,
            message: `Are you sure you want to ${actionText} the package "${app.name}"?`,
            type: "info",
            okLabel: actionText.charAt(0).toUpperCase() + actionText.slice(1),
            cancelLabel: "Cancel",
            showCancel: true,
            onOk: async () => {
                setToggling(true);
                try {
                    const payload = {
                        status: newStatus,
                    };

                    const response = await apiHelper.apiCall(`/arcee/application-pack/${app.id}/toggle-status/`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                    });

                    if (!response.ok) {
                        const errData = await response.json();
                        showAlertDialog({
                            title: "Update Failed",
                            message: errData.error || errData.message || response.statusText,
                            type: "error",
                            okLabel: "Close",
                            cancelLabel: "Close",
                            showCancel: false,
                        });
                        return;
                    }

                    const data = await response.json();

                    showAlertDialog({
                        title: "Success",
                        message: data.message || `Package ${actionText}d successfully!`,
                        type: "success",
                        okLabel: "Close",
                        showCancel: false,
                        onOk: () => {
                            packsQuery.refetch();
                        },
                    });

                } catch (error) {
                    console.error('Toggle error:', error);
                    showAlertDialog({
                        title: "Error",
                        message: "An error occurred while updating the status. Please try again later.",
                        type: "error",
                        okLabel: "Close",
                        showCancel: false,
                    });
                } finally {
                    setToggling(false);
                }
            },
            onCancel: () => {
                // Do nothing on cancel
            }
        });
    };


    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh', color: theme.primary }}>
                <span className="spinner-border" role="status" aria-hidden="true"></span>
                <span className="ms-2">Loading...</span>
            </div>
        );
    }

    if (error) {
        const displayError = error || "Something went wrong. Please try again.";
        return (
            <div style={{ height: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '10vh' }}>
                <MessageBox messageType="error" message={displayError} />
            </div>
        );
    }

    if (applications.length === 0) {
        return (
            <div style={{ height: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '10vh' }}>
                <MessageBox messageType="info" message="No Packages are present" />
            </div>
        );
    }

    return (
        <div className="d-flex flex-column align-items-center px-2 py-5 min-vh-100" style={{ background: "#f7f8fb" }}>
            <h2 className="mb-5" style={{ color: theme.primary, fontWeight: 700, letterSpacing: '.06em' }}>
                My Packages
            </h2>

            <div className="row w-100 gx-4 gy-5">
                {applications.map(app => (
                    <div key={app.id} className="col-12 col-md-6 col-lg-4 d-flex">
                        <ApplicationCard
                            app={{
                                ...app,
                                title: app.name,
                                state: app.status,
                                logoUrl: app.logo,
                                description: app.description,
                            }}
                            onOpen={handleOpen}
                            customAction={{
                                label: app.status === 'published' ? 'In-active' : 'Active',
                                icon: app.status === 'published' ? 'bi-eye-slash' : 'bi-eye',
                                onClick: () => handleToggleStatus(app),
                                color: app.status === 'published' ? '#f59e0b' : '#10b981'
                            }}
                            extraActions={[{
                                label: 'Copy Seed Code',
                                icon: 'bi-clipboard-data',
                                onClick: () => handleCopySeedCode(app),
                                color: theme.primary
                            }]}
                            showEdit={false}
                            showGenerateCredentials={false}
                            showDelete={false}
                            showInstall={false}
                            installed={app.installed}
                        />
                    </div>
                ))}
            </div>

            <Paginator
                page={page}
                pageCount={pageCount}
                onPrevious={handlePrevious}
                onNext={handleNext}
                onPage={handlePageChange}
                maxPagesToShow={3}
            />

            <ProgressBarDialog
                open={toggling}
                text_message="Updating status..."
                cancellable={false}
            />
        </div>
    );
}
